(() => {
  if (window.customElements?.get('ecomhard-kits')) return;

  class EcomhardKits extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.currency = this.dataset.currency || 'BRL';
      this.rootProductId = String(this.dataset.rootProductId || '');
      this.currentGalleryProductId = this.rootProductId;
      this.colorLabels = (this.dataset.colorLabels || 'Cor,Color,Colour')
        .split(',')
        .map((label) => this.normalize(label))
        .filter(Boolean);
      this.sizeLabels = (this.dataset.sizeLabels || 'Tamanho,Size')
        .split(',')
        .map((label) => this.normalize(label))
        .filter(Boolean);

      this.kitDiscountType = this.dataset.kitDiscountType || '';
      this.kitDiscountValue = this.dataset.kitDiscountValue || '';
      this.steps = Array.from(this.querySelectorAll('[data-kit-step]'));
      this.currentStep = 0;
      this.pricing = document.querySelector(`[data-ecomhard-kit-pricing="${this.id}"]`);
      this.sectionId = this.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '') || '';
      this.galleryCache = new Map();
      this.galleryRequestId = 0;
      this.isTransitioning = false;
      this.pricingMap = new Map();

      this.steps.forEach((step, index) => this.initializeStep(step, index));
      this.bindNavigation();
      this.bindSizeChart();
      this.showStep(0, false);
      this.updateAllPricing();

      if (this.steps[0]) {
        window.setTimeout(() => this.syncProductGallery(this.steps[0]), 0);
      }
    }

    normalize(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
    }

    handleize(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/['"]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    parseNumber(value) {
      if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
      const normalized = String(value || '')
        .trim()
        .replace(/\s/g, '')
        .replace(/\.(?=\d{3}(?:\D|$))/g, '')
        .replace(',', '.')
        .replace(/[^0-9.-]/g, '');
      const parsed = Number.parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    parseStepData(step) {
      try {
        const raw = step.querySelector('[data-kit-step-data]')?.textContent || '{}';
        const data = JSON.parse(raw);
        const unique = new Map();
        (data.products || []).forEach((product) => {
          if (product?.id && !unique.has(String(product.id))) unique.set(String(product.id), product);
        });
        data.products = Array.from(unique.values());
        return data;
      } catch (error) {
        console.error('[REV] Kits: dados inválidos da etapa.', error);
        return { products: [] };
      }
    }

    initializeStep(step, index) {
      step._kitData = this.parseStepData(step);
      step._state = {
        productId: String(step.dataset.baseProductId || step._kitData.baseProductId || ''),
        color: null,
        size: null,
        variant: null
      };

      const product = this.getSelectedProduct(step) || step._kitData.products[0];
      if (product) {
        step._state.productId = String(product.id);
        const initialVariant = (product.variants || []).find((variant) => variant.available) || product.variants?.[0] || null;
        const colorIndex = this.getColorOptionIndex(product);
        const sizeIndex = this.getSizeOptionIndex(product);

        if (colorIndex >= 0) {
          step._state.color = this.getVariantOption(initialVariant, colorIndex) || product.options?.[colorIndex]?.values?.[0] || null;
        }
        if (sizeIndex >= 0) {
          step._state.size = this.getVariantOption(initialVariant, sizeIndex) || this.getInitialSize(product, step._state.color);
        }
      }

      this.renderColors(step);
      this.renderSizes(step);
      this.updateStep(step);

      step.querySelector('[data-kit-colors]')?.addEventListener('change', (event) => {
        const input = event.target.closest('[data-kit-color]');
        if (!input || input.dataset.available !== 'true') return;

        const product = this.getSelectedProduct(step);
        const previousSize = step._state.size;
        step._state.color = input.value;

        if (product) {
          const sizeIndex = this.getSizeOptionIndex(product);
          const preserved = previousSize && this.isSizeAvailable(product, sizeIndex, previousSize, step._state.color);
          step._state.size = preserved ? previousSize : this.getInitialSize(product, step._state.color);
        }

        this.renderColors(step);
        this.renderSizes(step);
        this.updateStep(step);
        this.updateAllPricing();
        this.syncProductGallery(step);
        this.clearMessage(step);
      });

      step.querySelector('[data-kit-colors]')?.addEventListener('click', (event) => {
        const label = event.target.closest('label[aria-disabled="true"]');
        if (label) event.preventDefault();
      });

      step.querySelector('[data-kit-sizes]')?.addEventListener('change', (event) => {
        const input = event.target.closest('[data-kit-size]');
        if (!input || input.dataset.available !== 'true') return;

        step._state.size = input.value;
        this.updateStep(step);
        this.updateAllPricing();
        this.focusSelectedVariantMedia(step);
        this.clearMessage(step);
      });

      step.querySelector('[data-kit-sizes]')?.addEventListener('click', (event) => {
        const label = event.target.closest('label[aria-disabled="true"]');
        if (label) event.preventDefault();
      });

      step.dataset.ready = 'true';
      step.dataset.index = String(index);
    }

    getSelectedProduct(step) {
      return (step._kitData?.products || []).find(
        (product) => String(product.id) === String(step._state?.productId)
      );
    }

    getColorOptionIndex(product) {
      const options = product?.options || [];
      return options.findIndex((option) => this.colorLabels.includes(this.normalize(option.name)));
    }

    getSizeOptionIndex(product) {
      const options = product?.options || [];
      let index = options.findIndex((option) => this.sizeLabels.includes(this.normalize(option.name)));

      if (index === -1 && options.length === 1 && this.getColorOptionIndex(product) === -1) {
        const onlyName = this.normalize(options[0]?.name);
        if (onlyName && onlyName !== 'title' && onlyName !== 'titulo') index = 0;
      }

      return index;
    }

    getVariantOption(variant, index) {
      if (!variant || index < 0) return null;
      return Array.isArray(variant.options) ? variant.options[index] : null;
    }

    variantMatchesColor(product, variant, color) {
      const colorIndex = this.getColorOptionIndex(product);
      if (colorIndex < 0 || !color) return true;
      return String(this.getVariantOption(variant, colorIndex)) === String(color);
    }

    variantMatchesSize(product, variant, size) {
      const sizeIndex = this.getSizeOptionIndex(product);
      if (sizeIndex < 0 || !size) return true;
      return String(this.getVariantOption(variant, sizeIndex)) === String(size);
    }

    isColorAvailable(product, color) {
      return (product?.variants || []).some(
        (variant) => variant.available && this.variantMatchesColor(product, variant, color)
      );
    }

    isSizeAvailable(product, sizeIndex, size, color = null) {
      if (sizeIndex < 0) return true;
      return (product?.variants || []).some((variant) => {
        return variant.available &&
          this.variantMatchesColor(product, variant, color) &&
          String(this.getVariantOption(variant, sizeIndex)) === String(size);
      });
    }

    getInitialSize(product, color = null) {
      const sizeIndex = this.getSizeOptionIndex(product);
      if (sizeIndex < 0) return null;

      const optionValues = product?.options?.[sizeIndex]?.values || [];
      const firstAvailable = optionValues.find((value) => this.isSizeAvailable(product, sizeIndex, value, color));
      return firstAvailable || optionValues[0] || null;
    }

    getSelectedVariant(step) {
      const product = this.getSelectedProduct(step);
      if (!product) return null;

      const variants = product.variants || [];
      const matches = (variant) => {
        return this.variantMatchesColor(product, variant, step._state.color) &&
          this.variantMatchesSize(product, variant, step._state.size);
      };

      return variants.find((variant) => variant.available && matches(variant)) ||
        variants.find((variant) => matches(variant)) ||
        null;
    }

    getColorVariant(product, color) {
      const matching = (product?.variants || []).filter((variant) => this.variantMatchesColor(product, variant, color));
      return matching.find((variant) => variant.available) || matching[0] || null;
    }

    renderColors(step) {
      const wrapper = step.querySelector('[data-kit-colors-wrapper]');
      const container = step.querySelector('[data-kit-colors]');
      if (!wrapper || !container) return;

      const product = this.getSelectedProduct(step);
      const colorIndex = this.getColorOptionIndex(product);
      const values = colorIndex >= 0 ? (product?.options?.[colorIndex]?.values || []) : [];

      if (colorIndex < 0 || !values.length) {
        wrapper.hidden = true;
        container.innerHTML = '';
        return;
      }

      wrapper.hidden = false;
      container.innerHTML = '';

      values.forEach((value, valueIndex) => {
        const available = this.isColorAvailable(product, value);
        const variant = this.getColorVariant(product, value);
        const id = `${this.id}-step-${step.dataset.stepIndex}-color-${valueIndex}`;

        const input = document.createElement('input');
        input.className = 'product-form__radio visually-hidden';
        input.type = 'radio';
        input.name = `${this.id}-step-${step.dataset.stepIndex}-color`;
        input.id = id;
        input.value = value;
        input.dataset.kitColor = '';
        input.dataset.available = String(available);
        input.checked = String(step._state.color) === String(value);

        const label = document.createElement('label');
        label.className = `ecomhard-kits__color-option ${available ? 'available' : 'soldout'}${input.checked ? ' is-selected' : ''}`;
        label.htmlFor = id;
        label.title = value;
        if (!available) label.setAttribute('aria-disabled', 'true');

        const swatchImage = variant?.swatchImage || variant?.image || product?.featuredImage || '';
        if (swatchImage) {
          const image = document.createElement('img');
          image.src = swatchImage;
          image.alt = value;
          image.loading = 'lazy';
          label.appendChild(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'ecomhard-kits__color-placeholder';
          label.appendChild(placeholder);
        }

        container.append(input, label);
      });
    }

    renderSizes(step) {
      const wrapper = step.querySelector('[data-kit-sizes-wrapper]');
      const container = step.querySelector('[data-kit-sizes]');
      if (!wrapper || !container) return;

      const product = this.getSelectedProduct(step);
      const sizeIndex = this.getSizeOptionIndex(product);
      const values = sizeIndex >= 0 ? (product?.options?.[sizeIndex]?.values || []) : [];

      if (sizeIndex < 0 || !values.length) {
        wrapper.hidden = true;
        container.innerHTML = '';
        return;
      }

      wrapper.hidden = false;
      container.innerHTML = '';

      values.forEach((value, valueIndex) => {
        const available = this.isSizeAvailable(product, sizeIndex, value, step._state.color);
        const id = `${this.id}-step-${step.dataset.stepIndex}-size-${valueIndex}`;

        const input = document.createElement('input');
        input.className = 'product-form__radio visually-hidden';
        input.type = 'radio';
        input.name = `${this.id}-step-${step.dataset.stepIndex}-size`;
        input.id = id;
        input.value = value;
        input.dataset.kitSize = '';
        input.dataset.available = String(available);
        input.checked = String(step._state.size) === String(value);

        const label = document.createElement('label');
        label.className = `product-form__label ecomhard-kits__size-option ${available ? 'available' : 'soldout'}`;
        label.htmlFor = id;
        if (!available) {
          label.setAttribute('aria-disabled', 'true');
          label.title = 'Indisponível';
        }

        const text = document.createElement('span');
        text.className = 'text';
        text.textContent = value;
        label.appendChild(text);

        container.append(input, label);
      });
    }

    updateStep(step) {
      const product = this.getSelectedProduct(step);
      const variant = this.getSelectedVariant(step);
      step._state.variant = variant;
      if (!product) return;

      const title = step.querySelector('[data-kit-product-title]');
      const image = step.querySelector('.ecomhard-kits__summary-image');
      const selection = step.querySelector('[data-kit-selection]');

      if (title) title.textContent = product.title || '';

      if (image) {
        const nextImage = variant?.image || product.featuredImage;
        if (nextImage) {
          if (image.tagName === 'IMG') {
            image.removeAttribute('srcset');
            image.removeAttribute('sizes');
            image.src = nextImage;
            image.alt = product.title || '';
          } else {
            const nextImg = document.createElement('img');
            nextImg.className = 'ecomhard-kits__summary-image';
            nextImg.src = nextImage;
            nextImg.alt = product.title || '';
            nextImg.loading = 'lazy';
            image.replaceWith(nextImg);
          }
        }
      }

      const parts = [];
      if (step._state.color) parts.push(`Cor: ${step._state.color}`);
      if (step._state.size) parts.push(`Tamanho: ${step._state.size}`);
      if (selection) selection.textContent = parts.join(' • ');
    }

    applySingleDiscount(basePrice, type, rawValue) {
      const normalizedType = this.normalize(type);
      const value = this.parseNumber(rawValue);
      let discounted = Number(basePrice || 0);

      if (value > 0 && normalizedType.includes('porcent')) {
        discounted = Math.round(discounted * Math.max(0, 1 - value / 100));
      } else if (value > 0 && normalizedType.includes('fix')) {
        discounted = Math.max(0, discounted - Math.round(value * 100));
      }

      return discounted;
    }

    calculatePricing() {
      const records = this.steps.map((step, index) => {
        const variant = this.getSelectedVariant(step);
        const basePrice = Number(variant?.price || 0);
        const source = step.dataset.discountSource || 'none';
        const type = step.dataset.discountType || '';
        const value = step.dataset.discountValue || '';

        return {
          index,
          step,
          variant,
          basePrice,
          source,
          type,
          value,
          finalPrice: basePrice
        };
      });

      records.forEach((record) => {
        if (record.source === 'product') {
          record.finalPrice = this.applySingleDiscount(record.basePrice, record.type, record.value);
        }
      });

      const kitRecords = records.filter((record) => record.source === 'kit');
      const kitType = this.normalize(this.kitDiscountType);
      const kitValue = this.parseNumber(this.kitDiscountValue);

      if (kitRecords.length && kitValue > 0) {
        if (kitType.includes('porcent')) {
          kitRecords.forEach((record) => {
            record.finalPrice = this.applySingleDiscount(record.basePrice, this.kitDiscountType, this.kitDiscountValue);
          });
        } else if (kitType.includes('fix')) {
          const eligibleTotal = kitRecords.reduce((sum, record) => sum + record.basePrice, 0);
          let remainingDiscount = Math.min(eligibleTotal, Math.round(kitValue * 100));
          let remainingBase = eligibleTotal;

          kitRecords.forEach((record, recordIndex) => {
            let share = 0;
            if (remainingDiscount > 0 && remainingBase > 0) {
              if (recordIndex === kitRecords.length - 1) {
                share = remainingDiscount;
              } else {
                share = Math.min(record.basePrice, Math.round(remainingDiscount * (record.basePrice / remainingBase)));
              }
            }

            record.finalPrice = Math.max(0, record.basePrice - share);
            remainingDiscount -= share;
            remainingBase -= record.basePrice;
          });
        }
      }

      return records;
    }

    updateAllPricing() {
      const records = this.calculatePricing();
      this.pricingMap = new Map(records.map((record) => [record.step, record]));
      records.forEach((record) => this.updateStepPrice(record));
      this.updateKitPricing(records);
    }

    updateStepPrice(record) {
      const priceEl = record.step.querySelector('[data-kit-price]');
      const compareEl = record.step.querySelector('[data-kit-compare-price]');
      if (!priceEl || !compareEl || !record.variant) return;

      if (record.finalPrice < record.basePrice) {
        compareEl.textContent = this.formatMoney(record.basePrice);
        compareEl.hidden = false;
        priceEl.textContent = this.formatMoney(record.finalPrice);
      } else if (Number(record.variant.compareAtPrice || 0) > record.basePrice) {
        compareEl.textContent = this.formatMoney(Number(record.variant.compareAtPrice));
        compareEl.hidden = false;
        priceEl.textContent = this.formatMoney(record.basePrice);
      } else {
        compareEl.hidden = true;
        compareEl.textContent = '';
        priceEl.textContent = this.formatMoney(record.basePrice);
      }
    }

    updateKitPricing(records = this.calculatePricing()) {
      if (!this.pricing || !records.length) return;

      const regularTotal = records.reduce((sum, record) => sum + record.basePrice, 0);
      const kitTotal = records.reduce((sum, record) => sum + record.finalPrice, 0);
      const savings = Math.max(0, regularTotal - kitTotal);
      const discountPercent = regularTotal > 0 && savings > 0 ? Math.round((savings / regularTotal) * 100) : 0;

      const compareEl = this.pricing.querySelector('[data-kit-total-compare]');
      const priceEl = this.pricing.querySelector('[data-kit-total-price]');
      const promoEl = this.querySelector('[data-kit-promo]');
      const promoTitleEl = this.querySelector('[data-kit-promo-title]');
      const promoSubtitleEl = this.querySelector('[data-kit-promo-subtitle]');
      const badgeEl = this.pricing.querySelector('[data-kit-total-discount-badge]');
      const installmentsEl = this.pricing.querySelector('[data-kit-total-installments]');
      const pixEl = this.pricing.querySelector('[data-kit-total-pix]');

      if (priceEl) priceEl.textContent = this.formatMoney(kitTotal);

      if (compareEl) {
        compareEl.textContent = this.formatMoney(regularTotal);
        compareEl.hidden = savings <= 0;
      }

      if (badgeEl) {
        badgeEl.textContent = discountPercent > 0 ? `${discountPercent}% OFF` : '';
        badgeEl.hidden = discountPercent <= 0;
      }

      if (promoEl && promoTitleEl && promoSubtitleEl) {
        if (savings <= 0) {
          promoEl.hidden = true;
          promoTitleEl.textContent = '';
          promoSubtitleEl.textContent = '';
        } else {
          const productIds = records.map((record) => String(record.step.dataset.baseProductId || ''));
          const sameProduct = new Set(productIds).size === 1;
          const allProductDiscounts = records.every((record) => record.source === 'product');
          const firstDiscount = records[0] ? `${this.normalize(records[0].type)}|${this.parseNumber(records[0].value)}` : '';
          const sameDiscount = records.every((record) => `${this.normalize(record.type)}|${this.parseNumber(record.value)}` === firstDiscount);
          const sameFinalPrice = records.every((record) => record.finalPrice === records[0].finalPrice);
          const allKitDiscounts = records.every((record) => record.source === 'kit');
          const normalizedKitType = this.normalize(this.kitDiscountType);

          if (sameProduct && allProductDiscounts && sameDiscount && sameFinalPrice) {
            promoTitleEl.textContent = `nesse kit, cada peça sai por ${this.formatMoney(records[0].finalPrice)}`;
            promoSubtitleEl.textContent = discountPercent > 0 ? `economia de ${discountPercent}%` : '';
          } else {
            promoTitleEl.textContent = `nesse kit, você economiza ${this.formatMoney(savings)}`;

            if (allKitDiscounts && normalizedKitType.includes('porcent') && discountPercent > 0) {
              promoSubtitleEl.textContent = `economia de ${discountPercent}%`;
            } else if (allProductDiscounts && sameDiscount && this.normalize(records[0]?.type).includes('porcent') && discountPercent > 0) {
              promoSubtitleEl.textContent = `economia de ${discountPercent}%`;
            } else if (records.some((record) => record.source === 'product')) {
              promoSubtitleEl.textContent = 'desconto aplicado em cada peça';
            } else {
              promoSubtitleEl.textContent = '';
            }
          }

          promoEl.hidden = false;
        }
      }

      if (installmentsEl) {
        const maxInstallments = Math.max(1, Math.floor(this.parseNumber(this.pricing.dataset.installmentsMax || 10)));
        const minInstallmentCents = Math.max(0, Math.round(this.parseNumber(this.pricing.dataset.installmentMinValue || 0) * 100));
        let installments = maxInstallments;

        while (installments > 1 && minInstallmentCents > 0 && (kitTotal / installments) < minInstallmentCents) {
          installments -= 1;
        }

        if (installments > 1) {
          installmentsEl.textContent = `em ${installments}x sem juros de ${this.formatMoney(Math.floor(kitTotal / installments))}`;
        } else {
          installmentsEl.textContent = `à vista no cartão por ${this.formatMoney(kitTotal)}`;
        }
      }

      if (pixEl) {
        const pixPercent = Math.max(0, this.parseNumber(this.pricing.dataset.pixPercent || 0));
        const pixTotal = Math.max(0, Math.round(kitTotal * (1 - pixPercent / 100)));
        pixEl.textContent = `${this.formatMoney(pixTotal)} no pix`;
      }
    }

    getMainProductGallery() {
      const productView = this.closest('.halo-productView') || document.querySelector('.halo-productView');
      return productView?.querySelector('[data-image-gallery]') || null;
    }

    buildGalleryUrl(product) {
      const url = new URL(product.url, window.location.origin);
      if (this.sectionId) url.searchParams.set('section_id', this.sectionId);
      return url.toString();
    }

    async fetchGalleryMarkup(product) {
      const cacheKey = String(product.id);
      if (this.galleryCache.has(cacheKey)) return this.galleryCache.get(cacheKey);

      const galleryPromise = (async () => {
        const urls = [this.buildGalleryUrl(product)];
        const fullPageUrl = new URL(product.url, window.location.origin).toString();
        if (!urls.includes(fullPageUrl)) urls.push(fullPageUrl);

        for (const url of urls) {
          try {
            const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
            if (!response.ok) continue;

            const html = await response.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const gallery = doc.querySelector('[data-image-gallery]');
            if (gallery) return gallery.outerHTML;
          } catch (error) {
            console.warn('[REV] Kits: não foi possível carregar a galeria do produto.', error);
          }
        }

        return null;
      })();

      this.galleryCache.set(cacheKey, galleryPromise);
      const markup = await galleryPromise;
      if (!markup) this.galleryCache.delete(cacheKey);
      return markup;
    }

    reinitializeThemeGallery() {
      const productView = this.closest('.halo-productView') || document.querySelector('.halo-productView');
      if (!productView || !window.jQuery || !window.halo) return;
      const $scope = window.jQuery(productView);
      window.halo.productImageGallery?.($scope);
      window.halo.productVideoGallery?.($scope);
      window.halo.productCustomCursor?.($scope);
    }

    applyColorGalleryFilter(step) {
      const gallery = this.getMainProductGallery();
      if (!gallery || !window.jQuery) return;

      const color = step?._state?.color;
      const $gallery = window.jQuery(gallery);
      const $nav = $gallery.find('.productView-nav');
      const $for = $gallery.find('.productView-for');

      if (!$nav.length || !$for.length || !$nav.hasClass('slick-initialized') || !$for.hasClass('slick-initialized')) return;

      try {
        $nav.slick('slickUnfilter');
        $for.slick('slickUnfilter');

        if (color) {
          const selector = `.filter-${this.handleize(color)}`;
          if ($nav.find(selector).length && $for.find(selector).length) {
            $nav.slick('slickFilter', selector).slick('refresh');
            $for.slick('slickFilter', selector).slick('refresh');
          }
        }
      } catch (error) {
        console.warn('[REV] Kits: não foi possível filtrar a galeria pela cor.', error);
      }
    }

    focusSelectedVariantMedia(step) {
      const variant = this.getSelectedVariant(step);
      if (!variant?.mediaId || !window.jQuery) return;

      window.setTimeout(() => {
        const gallery = this.getMainProductGallery();
        if (!gallery) return;

        const exactSelector = this.sectionId
          ? `[data-media-id="${this.sectionId}-${variant.mediaId}"]`
          : `[data-media-id$="-${variant.mediaId}"]`;
        const media = gallery.querySelectorAll(exactSelector);
        if (!media.length) return;

        media.forEach((node) => window.jQuery(node).trigger('click'));
      }, 50);
    }

    async syncProductGallery(step) {
      const product = this.getSelectedProduct(step);
      if (!product?.url) return;

      const requestId = ++this.galleryRequestId;

      if (String(product.id) !== String(this.currentGalleryProductId)) {
        const currentGallery = this.getMainProductGallery();
        if (!currentGallery) return;
        currentGallery.setAttribute('aria-busy', 'true');

        try {
          const markup = await this.fetchGalleryMarkup(product);
          if (!markup || requestId !== this.galleryRequestId) return;

          const doc = new DOMParser().parseFromString(markup, 'text/html');
          const nextGallery = doc.querySelector('[data-image-gallery]');
          if (!nextGallery) return;

          const importedGallery = document.importNode(nextGallery, true);
          importedGallery.removeAttribute('aria-busy');
          currentGallery.replaceWith(importedGallery);
          this.currentGalleryProductId = String(product.id);
          this.reinitializeThemeGallery();
        } catch (error) {
          console.warn('[REV] Kits: erro ao trocar a galeria do produto.', error);
          return;
        }
      }

      if (requestId !== this.galleryRequestId) return;
      window.setTimeout(() => {
        this.applyColorGalleryFilter(step);
        this.focusSelectedVariantMedia(step);
      }, 80);
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', {
          style: 'currency',
          currency: this.currency
        }).format(amount);
      } catch (_) {
        return `R$ ${amount.toFixed(2).replace('.', ',')}`;
      }
    }

    isStepValid(step) {
      const product = this.getSelectedProduct(step);
      const variant = this.getSelectedVariant(step);
      if (!product || !variant || !variant.available) return false;

      const colorIndex = this.getColorOptionIndex(product);
      const sizeIndex = this.getSizeOptionIndex(product);
      if (colorIndex >= 0 && !step._state.color) return false;
      if (sizeIndex >= 0 && !step._state.size) return false;
      return true;
    }

    showMessage(step, message, isError = false) {
      const el = step.querySelector('[data-kit-message]');
      if (!el) return;
      el.textContent = message || '';
      el.classList.toggle('is-error', Boolean(isError));
    }

    clearMessage(step) {
      this.showMessage(step, '', false);
    }

    validateStep(step) {
      if (this.isStepValid(step)) {
        this.clearMessage(step);
        return true;
      }
      this.showMessage(step, this.dataset.errorSize || 'Selecione as opções disponíveis.', true);
      return false;
    }

    bindSizeChart() {
      this.sizeChartPopup = this.querySelector('[data-kit-size-chart-popup]');
      if (!this.sizeChartPopup) return;

      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-kit-size-chart]');
        const close = event.target.closest('[data-kit-close-size-chart]');

        if (trigger) {
          event.preventDefault();
          event.stopPropagation();
          const url = trigger.dataset.sizeChartUrl || '';
          const image = this.sizeChartPopup.querySelector('[data-kit-size-chart-image]');
          if (image && url) {
            image.removeAttribute('srcset');
            image.src = url;
          }
          document.body.classList.add('size-chart-show');
          this.sizeChartPopup.classList.add('is-show');
        }

        if (close) {
          event.preventDefault();
          this.closeSizeChart();
        }
      });

      this._sizeChartKeydown = (event) => {
        if (event.key === 'Escape' && this.sizeChartPopup?.classList.contains('is-show')) {
          this.closeSizeChart();
        }
      };
      document.addEventListener('keydown', this._sizeChartKeydown);

      const overlay = document.querySelector('.background-overlay');
      if (overlay) {
        this._sizeChartOverlayHandler = () => {
          if (this.sizeChartPopup?.classList.contains('is-show')) this.closeSizeChart();
        };
        overlay.addEventListener('click', this._sizeChartOverlayHandler);
      }
    }

    closeSizeChart() {
      document.body.classList.remove('size-chart-show');
      this.sizeChartPopup?.classList.remove('is-show');
    }

    disconnectedCallback() {
      if (this._sizeChartKeydown) document.removeEventListener('keydown', this._sizeChartKeydown);
      const overlay = document.querySelector('.background-overlay');
      if (overlay && this._sizeChartOverlayHandler) overlay.removeEventListener('click', this._sizeChartOverlayHandler);
    }

    bindNavigation() {
      this.addEventListener('click', (event) => {
        const next = event.target.closest('[data-kit-next]');
        const prev = event.target.closest('[data-kit-prev]');
        const addAll = event.target.closest('[data-kit-add-all]');

        if (next) {
          const step = next.closest('[data-kit-step]');
          if (!this.validateStep(step)) return;
          this.showStep(Math.min(this.currentStep + 1, this.steps.length - 1));
        }

        if (prev) this.showStep(Math.max(this.currentStep - 1, 0));
        if (addAll) this.addAllToCart(addAll);
      });
    }

    showStep(index, scroll = true) {
      if (!this.steps.length) return;

      const nextIndex = Math.max(0, Math.min(index, this.steps.length - 1));
      const currentIndex = this.currentStep;

      const scrollIfNeeded = () => {
        if (!scroll) return;
        const rect = this.getBoundingClientRect();
        if (rect.top < 0 || rect.top > window.innerHeight * 0.72) {
          this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      };

      if (nextIndex === currentIndex) {
        this.steps.forEach((step, stepIndex) => {
          const active = stepIndex === nextIndex;
          step.hidden = !active;
          step.classList.toggle('is-hidden', !active);
        });
        scrollIfNeeded();
        return;
      }

      if (this.isTransitioning) return;

      const currentStep = this.steps[currentIndex];
      const nextStep = this.steps[nextIndex];
      const direction = nextIndex > currentIndex ? 'forward' : 'backward';
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      this.currentStep = nextIndex;

      const finishSwap = () => {
        currentStep.hidden = true;
        currentStep.classList.add('is-hidden');
        currentStep.classList.remove('is-leaving-forward', 'is-leaving-backward');

        nextStep.hidden = false;
        nextStep.classList.remove('is-hidden');
        this.syncProductGallery(nextStep);
        scrollIfNeeded();
      };

      if (reduceMotion) {
        finishSwap();
        return;
      }

      this.isTransitioning = true;
      currentStep.classList.add(`is-leaving-${direction}`);

      window.setTimeout(() => {
        finishSwap();
        nextStep.classList.add(`is-entering-${direction}`);

        window.setTimeout(() => {
          nextStep.classList.remove('is-entering-forward', 'is-entering-backward');
          this.isTransitioning = false;
        }, 220);
      }, 170);
    }

    buildCartItems() {
      return this.steps.map((step, index) => {
        const product = this.getSelectedProduct(step);
        const variant = this.getSelectedVariant(step);
        const pricing = this.pricingMap.get(step);
        if (!product || !variant) return null;

        return {
          id: Number(variant.id),
          quantity: 1,
          properties: {
            _rev_kit_id: this.dataset.kitId || '',
            _rev_kit_title: this.dataset.kitTitle || '',
            _rev_kit_item_id: step.dataset.kitItemId || '',
            _rev_kit_step: `${index + 1}/${this.steps.length}`,
            _rev_kit_color: step._state.color || '',
            _rev_kit_size: step._state.size || '',
            _rev_kit_discount_scope: step.dataset.discountSource || 'none',
            _rev_kit_discount_type: step.dataset.discountType || '',
            _rev_kit_discount_value: step.dataset.discountValue || '',
            _rev_kit_group_discount_type: this.kitDiscountType || '',
            _rev_kit_group_discount_value: this.kitDiscountValue || '',
            _rev_kit_display_price: String(pricing?.finalPrice ?? variant.price ?? 0)
          }
        };
      }).filter(Boolean);
    }

    getCart() {
      if (window.Shopify?.getCart) {
        return new Promise((resolve) => window.Shopify.getCart(resolve));
      }
      const root = window.Shopify?.routes?.root || window.routes?.root || '/';
      return fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json());
    }

    wait(ms) {
      return new Promise((resolve) => window.setTimeout(resolve, ms));
    }

    async getSidebarCartHtml(expectedItemCount, maxAttempts = 6) {
      const root = window.Shopify?.routes?.root || window.routes?.root || '/';
      const normalizedRoot = root.endsWith('/') ? root : `${root}/`;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const response = await fetch(`${normalizedRoot}cart?view=ajax_side_cart&rev_kit=${Date.now()}-${attempt}`, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest',
            'Cache-Control': 'no-cache'
          }
        });

        if (response.ok) {
          const html = await response.text();
          const hasItemsMarkup = html.includes('previewCartItem') || html.includes('class="previewCart"');

          if ((expectedItemCount || 0) === 0 || hasItemsMarkup) {
            return html;
          }
        }

        await this.wait(140 + (attempt * 130));
      }

      return '';
    }

    hydrateThemeCartSidebar(html, cart) {
      const wrapper = document.querySelector('#halo-cart-sidebar .previewCart-wrapper');
      if (!wrapper || !html) return false;

      wrapper.classList.remove('is-loading');
      wrapper.innerHTML = html;

      document.querySelectorAll('[data-cart-count]').forEach((counter) => {
        counter.textContent = String(cart.item_count ?? 0);
      });

      document.querySelectorAll('[data-cart-text]').forEach((label) => {
        if (window.cartStrings) {
          label.textContent = cart.item_count === 1 ? window.cartStrings.item : window.cartStrings.items;
        }
      });

      try { window.halo?.dispatchChangeForShippingMessage?.(); } catch (error) {}
      try { window.halo?.productCollectionCartSlider?.(); } catch (error) {}
      try { window.halo?.updateGiftWrapper?.(); } catch (error) {}

      if (window.halo?.checkNeedToConvertCurrency?.()) {
        try {
          const currency = window.jQuery?.('#currencies .active')?.attr('data-currency');
          if (currency && window.Currency?.convertAll) {
            window.Currency.convertAll(window.shop_currency, currency, 'span.money', 'money_format');
          }
        } catch (error) {}
      }

      document.dispatchEvent(new CustomEvent('cart-update', { detail: cart }));
      return true;
    }

    async openThemeCart() {
      if (window.after_add_to_cart?.type === 'cart' || document.body.classList.contains('template-cart')) {
        window.location.href = window.routes?.cart || '/cart';
        return;
      }

      const cart = await this.getCart();
      const html = await this.getSidebarCartHtml(cart.item_count ?? 0);
      const hydrated = this.hydrateThemeCartSidebar(html, cart);

      if (!hydrated && window.halo?.updateSidebarCart) {
        window.halo.updateSidebarCart(cart);
        await this.wait(350);
      }

      document.querySelectorAll('[data-cart-count]').forEach((counter) => {
        counter.textContent = String(cart.item_count ?? 0);
      });

      document.body.classList.add('cart-sidebar-show');
      document.querySelector('.background-overlay')?.classList.remove('hold');
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart } }));
    }

    async addAllToCart(button) {
      const invalidIndex = this.steps.findIndex((step) => !this.isStepValid(step));
      if (invalidIndex !== -1) {
        this.showStep(invalidIndex);
        this.validateStep(this.steps[invalidIndex]);
        return;
      }

      if (button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');

      try {
        this.updateAllPricing();
        const root = window.Shopify?.routes?.root || window.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: this.buildCartItems() })
        });

        const responseJson = await response.json();
        if (!response.ok) {
          throw new Error(responseJson.description || responseJson.message || this.dataset.errorCart || 'Não foi possível adicionar o kit.');
        }

        await this.openThemeCart();
      } catch (error) {
        const step = this.steps[this.currentStep];
        this.showMessage(step, error.message || this.dataset.errorCart || 'Não foi possível adicionar o kit.', true);
      } finally {
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
      }
    }
  }

  window.customElements.define('ecomhard-kits', EcomhardKits);
})();
